"use client"

import * as React from "react"
import { format, parse, isValid, differenceInYears, isFuture } from "date-fns"
import { de } from "date-fns/locale"
import { CalendarIcon } from "lucide-react"

import { Calendar } from "./calendar"
import { Popover, PopoverContent, PopoverTrigger } from "./popover"

const MIN_AGE = 18
const MAX_AGE = 120

function validateBirthdate(date: Date): string | null {
  if (isFuture(date)) {
    return "Das Geburtsdatum darf nicht in der Zukunft liegen."
  }
  const age = differenceInYears(new Date(), date)
  if (age < MIN_AGE) {
    return `Sie müssen mindestens ${MIN_AGE} Jahre alt sein.`
  }
  if (age > MAX_AGE) {
    return "Bitte überprüfen Sie das eingegebene Geburtsdatum."
  }
  return null
}

export type BirthdayPickerProps = {
  /** ISO date string (YYYY-MM-DD) */
  value: string
  onChange: (isoDate: string) => void
  className?: string
  id?: string
  required?: boolean
}

export function BirthdayPicker({
  value,
  onChange,
  className,
  id,
}: BirthdayPickerProps) {
  const [open, setOpen] = React.useState(false)
  const [validationError, setValidationError] = React.useState<string | null>(
    null
  )

  const selectedDate = value
    ? parse(value, "yyyy-MM-dd", new Date())
    : undefined
  const isSelectedValid = selectedDate && isValid(selectedDate)

  const today = new Date()

  const handleSelect = (date: Date | undefined) => {
    if (!date) return

    const error = validateBirthdate(date)
    setValidationError(error)

    if (!error) {
      onChange(format(date, "yyyy-MM-dd"))
      setOpen(false)
    }
  }

  return (
    <div className="space-y-1.5">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            id={id}
            type="button"
            className={`${className ?? ""} flex items-center gap-3 text-left ${
              !isSelectedValid ? "text-muted-foreground" : ""
            }`}
          >
            <CalendarIcon className="h-5 w-5 shrink-0 text-muted-foreground" />
            {isSelectedValid
              ? format(selectedDate, "dd. MMMM yyyy", { locale: de })
              : "Geburtsdatum wählen"}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-auto rounded-[1.25rem] border-border/80 bg-popover p-2 shadow-xl"
          align="start"
        >
          <Calendar
            mode="single"
            selected={isSelectedValid ? selectedDate : undefined}
            onSelect={handleSelect}
            captionLayout="dropdown"
            className="rounded-2xl"
            fromYear={today.getFullYear() - MAX_AGE}
            toYear={today.getFullYear() - MIN_AGE}
            defaultMonth={
              isSelectedValid ? selectedDate : new Date(1990, 0)
            }
            locale={de}
            showOutsideDays={false}
            disabled={{ after: today }}
          />
        </PopoverContent>
      </Popover>
      {validationError && (
        <p className="text-sm text-destructive">{validationError}</p>
      )}
    </div>
  )
}
